// MP316c — Route + component wrapper for the platform feature-flag
// system.
//
// Wraps a route element (or any subtree) so it only renders when the
// named flag resolves enabled=true for the caller's org. When the
// flag is disabled the wrapper renders a `<Navigate>` back to
// `/launcher` (or the provided fallbackPath) — a hard block for
// direct URL access to a beta/off feature.
//
// Missing flag semantics (deliberate, matches the launcher's
// `hiddenAppKeys` pattern in LauncherPage.tsx):
//   • The client SDK is fail-safe: unknown flag key → enabled=false.
//   • But for a route gate, "flag not yet seeded" should default to
//     visible so the platform team can seed flags on their own
//     cadence without an outage window. We track visibility via the
//     resolved-flag set: only routes whose flag key exists AND
//     resolves to enabled=false are blocked. Missing keys pass
//     through (same policy as the launcher for app-level flags).
//   • Once the seed lands, the platform-console toggle takes over.
//
// Loading semantics:
//   • First render while `useAllFeatureFlags` is still fetching:
//     render children optimistically. A brief flash of a gated
//     surface is preferable to a flash of a redirect that then
//     bounces back once the flag resolves as enabled. The 60s poll +
//     module-scope cache mean subsequent navigations resolve
//     instantly.

//
// MP738 — standalone hosts. There `/launcher` itself redirects to the
// product's entry route, so a disabled flag on the entry route would
// bounce between the two forever. The fallback is resolved through the
// same standalone rule first, and when it lands back on this very page
// the wrapper shows a short "not available" notice instead.

import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { AppLayout } from './shell/AppLayout';
import { currentStandaloneBrand } from '../lib/tenantHost';
import { standaloneRedirectTarget } from '../lib/standaloneApp';
import { useAllFeatureFlags } from '../lib/useFeatureFlag';

interface Props {
  /** Flag key to gate on (e.g. 'xes.rules'). Must match the doc id
   *  under Firestore's `platform_feature_flags` collection. */
  flag: string;
  /** Where to bounce when the flag is explicitly disabled. Defaults
   *  to `/launcher` (the safe universal landing surface). */
  fallbackPath?: string;
  children: ReactNode;
}

export function FeatureFlagRoute({ flag, fallbackPath = '/launcher', children }: Props) {
  const { flags, loading } = useAllFeatureFlags();
  const { pathname } = useLocation();
  // Loading: render optimistically. Avoids a redirect flash on cold
  // navigation to a legitimately-enabled surface.
  if (loading) return <>{children}</>;
  const found = flags.find((f) => f.key === flag);
  // Missing flag → treat as ungated (see file header). Only an
  // existing-but-disabled flag redirects.
  if (found && !found.enabled) {
    const target = standaloneRedirectTarget(fallbackPath, currentStandaloneBrand()?.app ?? null) ?? fallbackPath;
    if (target === pathname) return <NotAvailableHere />;
    return <Navigate to={target} replace />;
  }
  return <>{children}</>;
}

function NotAvailableHere() {
  return (
    <AppLayout title="Not available yet">
      <div className="mx-auto max-w-lg px-4 py-16 text-center">
        <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">This isn’t switched on for your organization yet</h2>
        <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
          Your administrator or your account team can turn it on. You can still manage your organization and
          its people from the menu.
        </p>
      </div>
    </AppLayout>
  );
}

/** Compose helper: `useFeatureFlagVisible('xes.rules')` returns
 *  `true` when the flag either doesn't exist yet or resolves to
 *  enabled=true. Use for nav-item filtering (soft hide) — mirrors
 *  FeatureFlagRoute's missing-flag = visible semantics. */
export function useFeatureFlagVisible(flag: string): { visible: boolean; loading: boolean } {
  const { flags, loading } = useAllFeatureFlags();
  const found = flags.find((f) => f.key === flag);
  if (loading) return { visible: true, loading: true };
  if (!found) return { visible: true, loading: false };
  return { visible: found.enabled, loading: false };
}
